import {useRef} from 'react';
import {skuIsLoadedOrUnloadedVehicleSpecificEnclosure} from '@helpers/products/vehicle-specific.mjs';
import {DesktopClickableGallery} from '@components/product/desktop/DesktopClickableGallery';
import {ProductVariantSelector} from '@components/product/global/ProductVariantSelector';
import {Breadcrumbs} from '@components/product/global/Breadcrumbs';
import {handleScrollToReviews} from '@components/widgets/Common';
import {CustomerReviewsFromDb} from '@components/product/reviews/reviews-with-images';
import {PricesDesktop} from '@components/product/desktop/PricingDesktop';
import {BuildProductTabs} from '@components/product/global/BuildProductTabs';
import {FrequentlyBoughtTogether} from '@components/product/global/FrequentlyBoughtTogether';
import {AddToCartDesktop} from '@components/product/desktop/AddToCartDesktop';
import {InStockIndicator} from '@components/product/global/InStockIndicator';
import {ReviewStarsFromDb} from '@components/product/reviews/review-stars-from-db';
import {useProductProvider} from '@components/product/ProductProvider';
import {YoutubeEmbed} from '@components/media/YoutubeEmbed';
import {LowStockSalesPitch} from '@components/product/global/LowStockDisplay';
import {PriceLoader} from './global/PriceLoader';

export const ProductDetailsDesktop = () => {
  const {
    sku,
    variants,
    selectedVariant,
    vehicleFitment,
    breadcrumbs,
    frequentlyBoughtTogetherAssociations,
    currentVariantReviewData
  } = useProductProvider();
  // console.log("[ProductDetailsDesktop] selectedVariant:", selectedVariant);

  let breadcrumbSku = breadcrumbs?.breadCrumbLevel3Title;
  let categoryLink = breadcrumbs?.breadCrumbLevel1;
  let categoryName = breadcrumbs?.breadCrumbLevel1Title;
  let collectionLink = breadcrumbs?.breadCrumbLevel2;
  let collectionName = breadcrumbs?.breadCrumbLevel2Title;

  const reviewsAnchor = useRef(undefined);

  const hasVariants = variants?.length;
  const skuList = hasVariants ? variants?.map((variant) => variant?.sku) : [selectedVariant?.sku];

  let remappedSku = sku;
  if (sku?.includes('FORD-2009-UP')) {
    remappedSku = sku.replace('2009-UP', '2015-UP');
    breadcrumbSku = breadcrumbSku.replace('2009-UP', '2015-UP');
  }

  const avgScore = currentVariantReviewData?.avgScore;
  const reviewCount = currentVariantReviewData?.count;

  const siteName = selectedVariant?.siteName;
  const isDiscontinued = selectedVariant?.isDiscontinued === true;

  const productVideo = selectedVariant?.video?.url;

  const skuIsVehicleSpecific = skuIsLoadedOrUnloadedVehicleSpecificEnclosure(remappedSku);
  if (skuIsVehicleSpecific) {
    const vehicleMake = vehicleFitment?.vehicleMake;
    categoryLink = 'vehicle-specific-subwoofer-enclosures';
    categoryName = 'Vehicle Specific Enclosures';
    collectionLink = `/collections/vehicle-specific-subwoofer-enclosures?vehicle_make=${vehicleMake}`;
    collectionName = vehicleMake;
  }

  const selectedVariantPath = selectedVariant?.path ?? `/products/${selectedVariant?.handle}`;

  const isInStock = selectedVariant?.currentlyInStock ?? selectedVariant?.isInStock;

  const totalQtyWithoutFba =
    selectedVariant?.inventory?.total_qty_without_fba ?? selectedVariant?.total_qty_without_fba;
  // console.log('[InStockIndicator] totalQtyWithoutFba:', totalQtyWithoutFba);

  const tpaAndLvQtyCombinedRealOhTotal =
    (selectedVariant?.tpa_wh ?? 0) + (selectedVariant?.lv_wh ?? 0);

  const preorderInfo = selectedVariant?.preorderInfo; /*useProductStock?.pre_order_info;*/
  const isPreOrderEnabled = preorderInfo?.is_pre_order_enabled;
  const isPreOrderEligible = preorderInfo?.is_pre_order_eligible;

  // console.log("[ProductDetailsDesktop] selectedVariant:", selectedVariant);

  return (
    <div>
      <div className="max-w-[1300px] m-auto md:px-3">
        <Breadcrumbs
          aria-label="Breadcrumb"
          sku={breadcrumbSku}
          categoryLink={categoryLink}
          categoryName={categoryName}
          collectionLink={collectionLink}
          collectionName={collectionName}
          excludeCategoryLevel={false}
        />

        <div
          className="md:grid grid-cols-1 md:grid-cols-[6fr,4fr] gap-x-8"
          style={{gridTemplateColumns: 'minmax(0, 6fr) minmax(0, 4fr)'}}>
          <DesktopClickableGallery product={selectedVariant} variants={variants} />

          <div>
            {siteName && (
              <h1 className="text-2xl text-black mb-1 pt-6 pb-0" tabIndex="0">
                {siteName}
              </h1>
            )}

            {avgScore && (
              <a
                href={`${selectedVariantPath}#product-reviews`}
                onClick={(e) => {
                  handleScrollToReviews(e, reviewsAnchor);
                }}
                tabIndex="0"
                aria-label={`Review rating ${avgScore} out of 5 stars from ${reviewCount} reviews. Scroll down to read reviews.`}>
                <ReviewStarsFromDb avgScore={avgScore} count={reviewCount} />
              </a>
            )}

            <hr className="my-2" />

            <PriceLoader product={selectedVariant}>
              <PricesDesktop product={selectedVariant} />
            </PriceLoader>

            <ProductVariantSelector />

            <div>
              <div className="w-48 font-bold">
                <InStockIndicator product={selectedVariant} isDiscontinued={isDiscontinued} />
              </div>
            </div>

            {!isDiscontinued && isInStock && (
              <LowStockSalesPitch selectedVariant={selectedVariant} />
            )}

            <AddToCartDesktop />

            <BuildProductTabs
              selectedVariant={selectedVariant}
              variants={variants}
              isMobile={false}
            />

            {frequentlyBoughtTogetherAssociations?.length > 0 && (
              <div className={'mt-6 mb-6'}>
                <FrequentlyBoughtTogether products={frequentlyBoughtTogetherAssociations} />
              </div>
            )}
          </div>
        </div>
      </div>

      {productVideo && (
        <div className="bg-gray-100 flex justify-center py-4">
          <div className="w-full max-w-[1300px] flex justify-center">
            <div className="w-full px-2 md:w-8/12">
              <YoutubeEmbed url={productVideo} />
            </div>
          </div>
        </div>
      )}

      <div ref={reviewsAnchor} tabIndex="-1" id="product-reviews" className="outline-0">
        <CustomerReviewsFromDb
          sku={sku}
          skuList={skuList}
          selectedVariant={selectedVariant}
          currentVariantReviewData={currentVariantReviewData}
          isMobile={false}
        />
      </div>
    </div>
  );
};
